import React from 'react'
import { Box, Typography, Stack } from '@mui/material'
import { motion } from 'framer-motion'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../../constants/colors'
import { VisualizationRenderer } from './VisualizationRenderer'
import type { VisualizationRendererProps } from './VisualizationRenderer'
import { GlintReveal, GlintRevealOptions } from './GlintReveal'
import { DEFAULT_FADE_DURATION, DEFAULT_GLINT_DURATION, loadGlintDurationConfig } from '../constants/glintConfig'
import { EditorState } from 'draft-js'
import { Editor } from 'react-draft-wysiwyg'
import type { EditorProps } from 'react-draft-wysiwyg'
import DOMPurify from 'dompurify'
import { Button } from '../../../interaxDesignSystemComponents'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'
import { formatLlmText } from '../../facilitator/facilitatorReportFlow/helpers/textFormattingHelpers'
import { TeamDataSelector } from '../../facilitator/components/GenerateReport/visualizations'
import type { TeamPerformanceData } from '../../facilitator/components/GenerateReport/visualizations/teamInformationShares/types'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'

const DraftEditor = Editor as unknown as React.ComponentType<EditorProps>

const MotionBox = motion(Box)

/**
 * Common glass-morphism panel styles
 */
const GLASS_PANEL_STYLES = {
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '24px',
  flex: 1,
  background: `linear-gradient(144.63deg, ${INTERAX_COLORS.white08} 0%, ${INTERAX_COLORS.white04} 100%), ${INTERAX_COLORS.offBlack80}`,
  border: `1px solid ${INTERAX_COLORS.white08}`,
  boxShadow: `0px 12px 24px rgba(0, 0, 0, 0.25), inset 0px 1px 0px ${INTERAX_COLORS.white08}`,
  backdropFilter: 'blur(10px)',
  borderRadius: '16px',
  position: 'relative',
  overflow: 'hidden'
} as const

export interface TextPanelProps {
  /**
   * Tab label to display as title
   */
  label: string
  /**
   * Feedback text content
   */
  content: string
  /**
   * Whether the panel is currently in editing mode
   */
  isEditing?: boolean
  /**
   * Editor state for draft-js (when editing)
   */
  editorState?: EditorState | null
  /**
   * Callback when editor state changes
   */
  onEditorStateChange?: (state: EditorState) => void
  /**
   * Callback to save changes
   */
  onSave?: () => void
  /**
   * Callback to cancel editing
   */
  onCancel?: () => void
}

const sanitizedHtml = (html: string) => DOMPurify.sanitize(html || '')

/**
 * TextPanel - Left panel displaying text content
 */
export const TextPanel: React.FC<TextPanelProps> = ({
  label,
  content,
  isEditing = false,
  editorState,
  onEditorStateChange,
  onSave,
  onCancel
}) => {
  const { t } = useTranslation()
  // Format the content if it contains markdown-like formatting or HTML
  const formattedContent = React.useMemo(() => {
    // Check if content is already HTML (contains HTML tags)
    const isHtml = /<[a-z][\s\S]*>/i.test(content)
    if (isHtml) {
      // Content is already HTML, just sanitize it
      return sanitizedHtml(content)
    }
    // Check if content looks like it has markdown formatting
    const hasMarkdown =
      content.includes('**') || content.includes('\n\n') || /^\d+\.\s/.test(content) || /^[-•]\s/.test(content)
    if (hasMarkdown) {
      return formatLlmText(content)
    }
    // Otherwise, just sanitize plain text
    return sanitizedHtml(content)
  }, [content])

  // Determine if we should render as HTML (if it contains HTML tags or was formatted)
  const shouldRenderAsHtml = React.useMemo(() => {
    return /<[a-z][\s\S]*>/i.test(content) || formattedContent !== content
  }, [content, formattedContent])

  return (
    <Box
      sx={{
        ...GLASS_PANEL_STYLES,
        gap: '8px',
        position: 'relative'
      }}
    >
      <Typography
        sx={{
          ...INTERAX_TYPOGRAPHY.headline6,
          color: INTERAX_COLORS.white,
          alignSelf: 'stretch'
        }}
      >
        {label}
      </Typography>

      {isEditing && editorState && onEditorStateChange ? (
        <Stack spacing={2} sx={{ width: '100%', flexGrow: 1 }}>
          <Box
            sx={{
              backgroundColor: INTERAX_COLORS.white,
              borderRadius: '8px',
              padding: { xs: 1.5, sm: 1.5, md: 2 },
              border: `1px solid ${INTERAX_COLORS.thunderGrey20}`,
              minHeight: 240,
              flexGrow: 1
            }}
          >
            <DraftEditor
              editorState={editorState}
              onEditorStateChange={onEditorStateChange}
              toolbarClassName="rdw-toolbar-custom"
              editorClassName="rdw-editor-custom"
              editorStyle={{
                minHeight: 200,
                color: INTERAX_COLORS.thunderGrey,
                fontFamily: 'inherit',
                background: 'white'
              }}
              toolbarStyle={{
                border: 'none',
                backgroundColor: 'transparent'
              }}
              toolbar={{
                options: [
                  'inline',
                  'blockType',
                  'fontSize',
                  'fontFamily',
                  'list',
                  'textAlign',
                  'colorPicker',
                  'link',
                  'embedded',
                  'emoji',
                  'image',
                  'remove',
                  'history'
                ],
                inline: {
                  options: ['bold', 'italic', 'underline', 'strikethrough', 'monospace'],
                  bold: { className: 'rdw-option-wrapper' },
                  italic: { className: 'rdw-option-wrapper' },
                  underline: { className: 'rdw-option-wrapper' },
                  strikethrough: { className: 'rdw-option-wrapper' },
                  monospace: { className: 'rdw-option-wrapper' }
                },
                blockType: {
                  options: ['Normal', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'Blockquote', 'Code'],
                  className: 'rdw-option-wrapper'
                },
                fontSize: {
                  options: [8, 9, 10, 11, 12, 14, 16, 18, 24, 30, 36, 48, 60, 72, 96],
                  className: 'rdw-option-wrapper'
                },
                fontFamily: {
                  options: ['Arial', 'Georgia', 'Impact', 'Tahoma', 'Times New Roman', 'Verdana'],
                  className: 'rdw-option-wrapper'
                },
                list: {
                  options: ['unordered', 'ordered', 'indent', 'outdent'],
                  className: 'rdw-option-wrapper'
                },
                textAlign: {
                  options: ['left', 'center', 'right', 'justify'],
                  className: 'rdw-option-wrapper'
                },
                colorPicker: {
                  className: 'rdw-option-wrapper',
                  colors: ['#000000', '#FF0000', '#0000FF', '#00FF00', '#FFFF00', '#FF00FF', '#00FFFF', '#FFFFFF']
                },
                link: {
                  className: 'rdw-option-wrapper',
                  popupClassName: 'rdw-link-popup',
                  options: ['link', 'unlink']
                },
                history: {
                  options: ['undo', 'redo'],
                  className: 'rdw-option-wrapper'
                }
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={onSave}>
              {t('common.save')}
            </Button>
          </Box>
        </Stack>
      ) : (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            flexGrow: 1,
            minHeight: '120px',
            maxHeight: '500px',
            overflowY: 'auto',
            // Hide scrollbar but keep scrolling functional
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE and Edge
            '&::-webkit-scrollbar': {
              display: 'none' // Chrome, Safari, Opera
            }
          }}
        >
          {shouldRenderAsHtml && formattedContent ? (
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body3,
                color: INTERAX_COLORS.typographyFont,
                alignSelf: 'stretch',
                flexGrow: 1,
                lineHeight: 1.6,
                // Force color on all nested elements to override inline styles
                '& *': {
                  color: `${INTERAX_COLORS.typographyFont} !important`
                },
                '& p': {
                  marginBottom: '12px',
                  color: `${INTERAX_COLORS.typographyFont} !important`,
                  '&:last-child': {
                    marginBottom: 0
                  }
                },
                '& ol, & ul': {
                  marginLeft: '20px',
                  marginBottom: '12px',
                  color: `${INTERAX_COLORS.typographyFont} !important`,
                  '& li': {
                    marginBottom: '8px',
                    color: `${INTERAX_COLORS.typographyFont} !important`
                  }
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                  color: `${INTERAX_COLORS.typographyFont} !important`
                },
                '& strong, & b': {
                  fontWeight: 600,
                  color: `${INTERAX_COLORS.typographyFont} !important`
                },
                '& div, & span': {
                  color: `${INTERAX_COLORS.typographyFont} !important`
                }
              }}
              dangerouslySetInnerHTML={{ __html: formattedContent }}
            />
          ) : (
            <Typography
              sx={{
                ...INTERAX_TYPOGRAPHY.body3,
                color: INTERAX_COLORS.typographyFont,
                alignSelf: 'stretch',
                flexGrow: 1,
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6
              }}
            >
              {content}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

export interface VisualizationPanelProps {
  /**
   * Visualization configuration
   */
  visualization: VisualizationRendererProps['visualization']
  /**
   * Metric name for the visualization
   */
  metricName: string
  /**
   * Variable name for the visualization
   */
  variableName?: string
  /**
   * Context (strengths or weaknesses)
   */
  context?: VisualizationRendererProps['context']
  /**
   * Variant for styling
   */
  variant: VisualizationRendererProps['variant']
  /**
   * Additional data for visualization
   */
  additionalData?: VisualizationRendererProps['additionalData']
  /**
   * Incremented value to trigger the glint animation
   */
  glintTrigger?: number
  /**
   * Duration override for the glint animation (in seconds)
   */
  glintDuration?: number
  /**
   * Duration (in seconds) for the overlay fade
   */
  fadeDuration?: number
  /**
   * Total time (in seconds) for the full reveal experience
   */
  experienceDuration?: number
  /**
   * Configuration overrides for the glint animation
   */
  glintOptions?: GlintRevealOptions
  /**
   * Controls first-visit glow experience overlay visibility
   */
  showGlowExperience?: boolean
  /**
   * Callback fired when the glow experience has been completed
   */
  onGlowExperienceComplete?: () => void
  /**
   * Notifies when the glint animation state changes
   */
  onGlintStateChange?: (state: 'idle' | 'running') => void
  /**
   * Team data for team selector (when visualization uses teamData)
   */
  teamData?: Array<{
    teamId: string
    teamName: string
  }>
  /**
   * Currently selected team IDs (array of selected team IDs, empty means show all teams/aggregate)
   */
  selectedTeamIds?: string[]
  /**
   * Callback to update selected teams
   */
  onTeamChange?: (selectedTeamIds: string[]) => void
}

/**
 * VisualizationPanel - Right panel displaying visualization
 */
export const VisualizationPanel: React.FC<VisualizationPanelProps> = ({
  visualization,
  metricName,
  variableName,
  context,
  variant,
  additionalData,
  glintTrigger = 0,
  glintDuration,
  fadeDuration,
  experienceDuration,
  glintOptions,
  showGlowExperience = false,
  onGlowExperienceComplete,
  onGlintStateChange,
  teamData,
  selectedTeamIds,
  onTeamChange
}) => {
  const [isOverlayActive, setIsOverlayActive] = React.useState(showGlowExperience)
  const [overlayState, setOverlayState] = React.useState<'hidden' | 'blur' | 'fade'>(
    showGlowExperience ? 'blur' : 'hidden'
  )
  const [glintPhase, setGlintPhase] = React.useState<'idle' | 'running'>('idle')
  const glintCompletedRef = React.useRef(!showGlowExperience)
  const lastTriggerRef = React.useRef(glintTrigger)
  const fadeStartTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const overlayHideTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const storedDurations = React.useMemo(() => loadGlintDurationConfig(), [])
  const glintDurationSec = glintDuration ?? storedDurations.glintDuration ?? DEFAULT_GLINT_DURATION
  const fadeDurationSec = fadeDuration ?? storedDurations.fadeDuration ?? DEFAULT_FADE_DURATION
  const experienceDurationSec =
    experienceDuration ?? storedDurations.experienceDuration ?? glintDurationSec + fadeDurationSec

  React.useEffect(() => {
    if (showGlowExperience) {
      setIsOverlayActive(true)
      setOverlayState('blur')
      setGlintPhase('idle')
      glintCompletedRef.current = false
      return
    }

    setIsOverlayActive(false)
    setOverlayState('hidden')
    setGlintPhase('idle')
    glintCompletedRef.current = true
    if (overlayHideTimeoutRef.current) {
      clearTimeout(overlayHideTimeoutRef.current)
      overlayHideTimeoutRef.current = null
    }
    if (fadeStartTimeoutRef.current) {
      clearTimeout(fadeStartTimeoutRef.current)
      fadeStartTimeoutRef.current = null
    }
    lastTriggerRef.current = glintTrigger
  }, [glintTrigger, showGlowExperience])

  React.useEffect(() => {
    if (!showGlowExperience) return
    if (glintTrigger === 0 || glintTrigger === lastTriggerRef.current) return

    setIsOverlayActive(true)
    setOverlayState('blur')
    setGlintPhase('running')
    glintCompletedRef.current = false
    if (overlayHideTimeoutRef.current) {
      clearTimeout(overlayHideTimeoutRef.current)
      overlayHideTimeoutRef.current = null
    }
    if (fadeStartTimeoutRef.current) {
      clearTimeout(fadeStartTimeoutRef.current)
      fadeStartTimeoutRef.current = null
    }

    const fadeStartDelaySec = Math.max(experienceDurationSec - fadeDurationSec, 0)
    const handleFadeStart = () => {
      fadeStartTimeoutRef.current = null
      setOverlayState('fade')
      if (fadeDurationSec <= 0) {
        setOverlayState('hidden')
        setIsOverlayActive(false)

        onGlowExperienceComplete?.()
        return
      }
      overlayHideTimeoutRef.current = setTimeout(() => {
        setOverlayState('hidden')
        setIsOverlayActive(false)
        overlayHideTimeoutRef.current = null

        onGlowExperienceComplete?.()
      }, fadeDurationSec * 1000)
    }

    if (fadeStartDelaySec <= 0) {
      handleFadeStart()
    } else {
      fadeStartTimeoutRef.current = setTimeout(handleFadeStart, fadeStartDelaySec * 1000)
    }
    lastTriggerRef.current = glintTrigger
  }, [experienceDurationSec, fadeDurationSec, glintTrigger, metricName, onGlowExperienceComplete, showGlowExperience])

  React.useEffect(() => {
    return () => {
      glintCompletedRef.current = true
      if (overlayHideTimeoutRef.current) {
        clearTimeout(overlayHideTimeoutRef.current)
      }
      if (fadeStartTimeoutRef.current) {
        clearTimeout(fadeStartTimeoutRef.current)
      }
    }
  }, [])

  const handleGlintComplete = React.useCallback(() => {
    if (glintCompletedRef.current) return
    glintCompletedRef.current = true
    setGlintPhase('idle')
  }, [])

  React.useEffect(() => {
    onGlintStateChange?.(glintPhase)
  }, [glintPhase, onGlintStateChange])

  // Filter chart data based on selected team IDs
  const filteredAdditionalData = React.useMemo(() => {
    // If no chartData or teamData, return as-is
    if (!additionalData?.chartData || !teamData) {
      return additionalData
    }

    // If no teams selected (empty array) or selectedTeamIds is undefined/null, show all teams (default behavior)
    if (!selectedTeamIds || selectedTeamIds.length === 0) {
      return additionalData
    }

    const chartData = additionalData.chartData as TeamPerformanceData[]

    // Map team IDs to their position index (teamData[0] = team1Shares, teamData[1] = team2Shares)
    const team1Id = teamData[0]?.teamId
    const team2Id = teamData[1]?.teamId
    const showTeam1 = team1Id && selectedTeamIds.includes(team1Id)
    const showTeam2 = team2Id && selectedTeamIds.includes(team2Id)

    // If both teams are selected (or all teams), return original data
    if (showTeam1 && showTeam2) {
      return additionalData
    }

    // Filter chart data based on selected teams
    // Note: We filter by omitting team1Shares/team2Shares when teams are not selected
    // The chart component checks for undefined to conditionally render lines
    const filteredChartData = chartData.map((dataPoint) => {
      const filtered: Partial<TeamPerformanceData> & { period: number } = {
        period: dataPoint.period
      }

      // Only include team1Shares if team1 is selected
      if (showTeam1) {
        filtered.team1Shares = dataPoint.team1Shares
      }

      // Only include team2Shares if team2 is selected
      if (showTeam2 && dataPoint.team2Shares !== undefined) {
        filtered.team2Shares = dataPoint.team2Shares
      }

      // Type assertion: Chart component handles undefined values correctly
      return filtered as TeamPerformanceData
    })

    return {
      ...additionalData,
      chartData: filteredChartData
    }
  }, [additionalData, teamData, selectedTeamIds])

  return (
    <Box
      sx={{
        ...GLASS_PANEL_STYLES,
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '10px',
        gap: '10px'
      }}
    >
      {/* Team Data Selector - show when teamData is available */}
      {teamData && teamData.length > 0 && onTeamChange && (
        <TeamDataSelector
          teams={teamData}
          selectedTeamIds={selectedTeamIds ?? []}
          onTeamChange={(teamIds: string[]) => onTeamChange?.(teamIds)}
        />
      )}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <VisualizationRenderer
          visualization={visualization}
          metricName={metricName}
          variableName={variableName}
          context={context}
          variant={variant}
          additionalData={filteredAdditionalData}
        />
      </Box>
      {isOverlayActive && (
        <>
          <MotionBox
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: 'inherit',
              pointerEvents: 'none',
              background: `linear-gradient(148deg, ${INTERAX_COLORS.heritageBlue40} 0%, ${INTERAX_COLORS.white08} 100%)`,
              border: `1px solid ${INTERAX_COLORS.white04}`,
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.12)',
              backdropFilter: 'blur(12px)'
            }}
            initial={{ opacity: 1 }}
            animate={{ opacity: overlayState === 'fade' ? 0 : 1 }}
            transition={{
              duration: overlayState === 'fade' ? Math.max(fadeDurationSec, 0.01) : 0.1,
              ease: 'easeInOut'
            }}
          />
          <GlintReveal
            active={glintPhase === 'running'}
            trigger={glintTrigger}
            glintDuration={glintDurationSec}
            onComplete={handleGlintComplete}
            options={glintOptions}
          />
        </>
      )}
    </Box>
  )
}

export default { TextPanel, VisualizationPanel }
